// @fileoverview A11y helpers for post descriptions
import { n__, p__, __ } from '@@/bits/intl'
import { isPhotoAlbum, isTweet } from '@@/bits/post_attachment'
import { getCustomPropertyValue, getPostPropertyFormattedValue } from '@@/bits/post_properties'
import { getPollFromPost, hasPollAttachment } from '@@/bits/surface_polls'
import { attachmentCaptionForDisplay } from '@@/surface/attachment_caption_for_display'
import type { PostAttributes, WallCustomPostProperty } from '@@/types'
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { isEmpty } from 'es-toolkit/compat'

export interface PostAriaDetails {
  description: string
  isDescriptionGeneratedFromAttachment: boolean
}

export const getPostAriaDetails = async (
  post: PostAttributes,
  attachmentDisplayAttributes?: BeethovenDisplayAttributes | null,
  wallCustomPostProperties?: WallCustomPostProperty[],
): Promise<PostAriaDetails> => {
  let description = ''
  let isDescriptionGeneratedFromAttachment = true

  if (!isEmpty(post.subject)) {
    isDescriptionGeneratedFromAttachment = false
    description = post.subject as string
    return {
      description,
      isDescriptionGeneratedFromAttachment,
    }
  }
  if (!isEmpty(post.attachment_caption)) {
    description =
      attachmentDisplayAttributes != null
        ? attachmentCaptionForDisplay({
            attachmentCaption: post.attachment_caption,
            attachmentDisplayAttributes,
          })
        : (post.attachment_caption as string)
    return {
      description,
      isDescriptionGeneratedFromAttachment,
    }
  }
  if (!isEmpty(post.wish_content?.attachment_props?.alternative_text)) {
    description = post.wish_content?.attachment_props?.alternative_text as string
    return {
      description,
      isDescriptionGeneratedFromAttachment,
    }
  }
  if (isPhotoAlbum(post)) {
    const firstPhotoAlt = post.attachments[0].alt
    description = firstPhotoAlt
      ? p__(
          'Describes the photo album attachment with the alt text of the first photo and the number of additional photos',
          'Photo album with "%{firstPhotoAlt}" and %{count} more photos',
          {
            firstPhotoAlt,
            count: post.attachments.length - 1,
          },
        )
      : __('Photo album with %{count} photos', {
          count: post.attachments.length,
        })
    return {
      description,
      isDescriptionGeneratedFromAttachment,
    }
  }
  if (isTweet(post.wish_content?.attachment_props)) {
    const tweetText = post.wish_content?.attachment_props?.text
    const tweetImagesLength = post.wish_content?.attachment_props?.attachments?.media?.length ?? 0
    description =
      tweetImagesLength > 0
        ? n__(
            'Tweet about %{tweetText} with %{tweetImagesLength} image',
            'Tweet about %{tweetText} with %{tweetImagesLength} images',
            tweetImagesLength,
            { tweetText, tweetImagesLength },
          )
        : __('Tweet about %{tweetText}', { tweetText })
    return {
      description,
      isDescriptionGeneratedFromAttachment,
    }
  }
  if (hasPollAttachment(post)) {
    const poll = getPollFromPost(post)
    description = __('Poll about %{pollText}', {
      pollText: poll?.question ?? '',
    })
    return {
      description,
      isDescriptionGeneratedFromAttachment,
    }
  }

  if (post.custom_properties && !isEmpty(post.custom_properties) && wallCustomPostProperties) {
    for (const property of wallCustomPostProperties) {
      const value = getCustomPropertyValue(post, property)
      if (value !== '' && value !== undefined && value != null) {
        const formattedValue = await getPostPropertyFormattedValue({ property, value })
        if (formattedValue) {
          description = `${property.name}: ${formattedValue}`
          isDescriptionGeneratedFromAttachment = false
          return {
            description,
            isDescriptionGeneratedFromAttachment,
          }
        }
      }
    }
  }

  // After all other checks, if there's still no description, use the post body assuming that is minimally filled
  if (!isEmpty(post.body)) {
    description = post.body ?? ''
    isDescriptionGeneratedFromAttachment = false
  }

  return {
    description,
    isDescriptionGeneratedFromAttachment,
  }
}

export const getPostAttachmentAriaLabelForExpanding = async ({
  post,
  attachmentDisplayAttributes,
  wallCustomPostProperties,
}: {
  post: PostAttributes
  attachmentDisplayAttributes?: BeethovenDisplayAttributes | null
  wallCustomPostProperties?: WallCustomPostProperty[]
}): Promise<string> => {
  const { description, isDescriptionGeneratedFromAttachment } = await getPostAriaDetails(
    post,
    attachmentDisplayAttributes,
    wallCustomPostProperties,
  )
  return isDescriptionGeneratedFromAttachment
    ? __('Open the post with attachment of %{description} in expanded view', {
        description,
      })
    : __('Open the post %{description} in expanded view', {
        description,
      })
}

export const getPostAttachmentAnchorAriaLabel = ({
  interaction,
  attachmentDisplayAttributes,
  post,
}: {
  interaction: string
  attachmentDisplayAttributes?: BeethovenDisplayAttributes | null
  post: PostAttributes
}): string => {
  if (interaction === 'link') {
    // we want the link to be read as is
    return ''
  }

  if (interaction === 'download') {
    const fileType = `${attachmentDisplayAttributes?.content_subcategory ?? ''} ${
      attachmentDisplayAttributes?.content_category ?? ''
    }`.trim()
    return fileType ? __('Download %{fileDescription}', { fileDescription: fileType }) : __('Download file')
  }

  if (interaction === 'play') {
    const mediaType = attachmentDisplayAttributes?.content_category
    if (mediaType === 'audio') return __('Play audio')
    if (mediaType === 'video') return __('Play video')
    return __('Play media')
  }

  // For 'expand' interaction
  return __('Open attachment in expanded view')
}
